// THE LINE-ITEM TABLES' COLUMN WIDTHS, AS DATA (QA 2026-09-30, C1-01 and C2-02).
//
// Both line-item tables use `table-layout: fixed`: the sized columns (the select
// box, the expand column, the amounts, the icons) take their stated width, and the unsized text
// columns (Description, and Account / Department / Code / Company where they
// have no width) split whatever is left. The table's `min-width` is the only
// thing that stops the text columns being crushed; below it, the table scrolls
// inside its own wrapper instead.
//
// The two floors were hand-added sums, and both were wrong by the same two
// things. Fluent's `TableHeaderCell` pads 8px each side, and the cell is
// `box-sizing: content-box` by the browser's default for a table cell
// (Fluent sets `content-box` only on the cell's inner button), so the padding
// sits outside the stated width and every column is 16px wider than it, and
// D-228's select column arrived without the floor moving. At 953px the QA
// tester measured Description at 14px in the editor, and Description, Account
// and Department at 43px each in the read-only table. The floor is now worked
// out here, from the same widths the headers render, so adding a column moves
// it.

/** Fluent's small `TableHeaderCell` pads 8px each side; the browser's default
    `content-box` puts that padding outside its width. */
export const CELL_PADDING_X = 16

/** The narrowest a text column may be drawn, before its padding. */
export const TEXT_COLUMN_FLOOR = {
  description: 180,
  coding: 140,
} as const

/** The stated widths of the read-only `Line items` table's sized columns. */
export const READ_ONLY_WIDTHS = {
  select: 36,
  /** THE EXPAND COLUMN, where # was (owner, 2026-10-22: "For now, let's just
      plainly hide the column."): a group row's expand arrow, and Expand All /
      Collapse All in the header. With the select column, it is the bulk row's
      Action picker's room. */
  expand: 44,
  price: 100,
  quantity: 90,
  tax: 84,
  shipping: 90,
  total: 100,
  /** The icon column: one icon, Additional Detail, since the Associated
      Entities folded into it (owner, 2026-10-28 — D-425; 68 until then). */
  details: 40,
  /** The icon column where lines can be checked: room for a second icon, the
      bulk row's Unselect All beside Additional Detail (D-242; 96 until D-425). */
  detailsSelectable: 68,
} as const

/** The read-only table's icon column: wider where lines can be checked (D-242). */
export function readOnlyDetailsWidth(select: boolean): number {
  return select ? READ_ONLY_WIDTHS.detailsSelectable : READ_ONLY_WIDTHS.details
}

/** The stated widths of the Import Control editor's sized columns. Account and
    Department are sized here, so only Description flexes. */
export const EDITOR_WIDTHS = {
  select: 36,
  /** The expand column, as `READ_ONLY_WIDTHS.expand`. */
  expand: 44,
  account: 190,
  department: 170,
  price: 96,
  quantity: 76,
  tax: 84,
  shipping: 90,
  total: 104,
  /** Additional Detail and Delete, or Remove Group on a group row, or the bulk
      row's Unselect All (D-425; 96 while Associated Entities had its own icon). */
  actions: 68,
} as const

/** A fixed-layout table's floor: every sized column plus every text column's
    floor, each with its padding. */
export function fixedTableMinWidth(sized: readonly number[], textFloors: readonly number[]): number {
  const pad = (w: number) => w + CELL_PADDING_X
  return [...sized, ...textFloors].reduce((sum, w) => sum + pad(w), 0)
}

/**
 * WHETHER THE EXPAND COLUMN SHOWS: wherever a group row can (Show Group Rows on,
 * over a group), and wherever lines can be checked, because the bulk row's
 * Action picker spans it and the select column. Elsewhere there is no column
 * where # was.
 */
export function showsExpandColumn(c: { select: boolean; groupRows: boolean }): boolean {
  return c.select || c.groupRows
}

interface ReadOnlyColumns {
  select: boolean
  /** The expand column (`showsExpandColumn`). */
  expand: boolean
  code: boolean
  account: boolean
  department: boolean
  distributing: boolean
}

/** The read-only table's floor for the columns it is showing. */
export function readOnlyLineTableMinWidth(c: ReadOnlyColumns): number {
  const w = READ_ONLY_WIDTHS
  const sized = [
    ...(c.select ? [w.select] : []),
    ...(c.expand ? [w.expand] : []),
    w.price,
    w.quantity,
    ...(c.distributing ? [w.tax, w.shipping] : []),
    w.total,
    readOnlyDetailsWidth(c.select),
  ]
  const text = [
    TEXT_COLUMN_FLOOR.description,
    ...(c.code ? [TEXT_COLUMN_FLOOR.coding] : []),
    ...(c.account ? [TEXT_COLUMN_FLOOR.coding] : []),
    ...(c.department ? [TEXT_COLUMN_FLOOR.coding] : []),
  ]
  return fixedTableMinWidth(sized, text)
}

interface EditorColumns {
  select: boolean
  /** The expand column (`showsExpandColumn`). */
  expand: boolean
  distributing: boolean
}

/** The editor's floor for the columns it is showing. */
export function editorLineTableMinWidth(c: EditorColumns): number {
  const w = EDITOR_WIDTHS
  const sized = [
    ...(c.select ? [w.select] : []),
    ...(c.expand ? [w.expand] : []),
    w.account,
    w.department,
    w.price,
    w.quantity,
    ...(c.distributing ? [w.tax, w.shipping] : []),
    w.total,
    w.actions,
  ]
  return fixedTableMinWidth(sized, [TEXT_COLUMN_FLOOR.description])
}
